import React, { useEffect, useRef, useState } from 'react';

import { Alert, Box, Button, CircularProgress, Grid, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import KpiComponent from '@/components/Base/KpiComponent';
import dayjs from 'dayjs';
import ImportCountDefault from '@/components/Base/ImportCountDefault';
import { ArrowBack, Search } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useConsolidationSummaryStudents } from '@/features/dashboard/api/getConsolidationSummaryStudents';
import { TitlePages } from '@/components/Base/TitlePage';
import Thermometer from '@/components/Base/Thermometer';
import { useTermometroFinanceiroDetalhes } from '@/features/termometro/getTermometroFinanceiroDetalhes';
import TableTermometro from '@/components/Base/TableTermometro';
import { useTermometroFrequencia } from '@/features/termometro/getTermometroFrequencia';

const generateLastYears = (numYears) => {
  const currentYear = dayjs().year();
  return Array.from({ length: numYears }, (_, index) => currentYear - index).reverse();
};

const getPositionFromScore = (month_score) => {
  if (month_score >= 0 && month_score < 3.33) {
    return 'end';
  } else if (month_score >= 3.33 && month_score < 6.66) {
    return 'center';
  } else if (month_score >= 6.66 && month_score <= 10) {
    return 'start';
  } else {
    return 'end';
  }
};

export const TermometroFrequencia = ({ routeBack }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const startDate = dayjs().startOf('year').format('YYYY-MM-DD');
  const endDate = dayjs().endOf('month').format('YYYY-MM-DD');
  const inputRef = useRef<HTMLInputElement>(null);
  const { data: summary, isError, refetch, isFetching, isFetched } = useTermometroFrequencia({
    name: searchQuery || undefined
  });

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && document.activeElement === inputRef.current) {
        setSearchQuery(inputRef.current?.value || '');
        refetch();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [refetch]);

  const [open, setOpen] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState(startDate);
  const [selectedEndDate, setSelectedEndDate] = useState(endDate);

  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  // const [listYears, setListYears] = useState(generateLastYears(5));

  const location = useLocation();
  const { id, name } = location.state || {};


  const navigate = useNavigate();

  const handleClose = () => {
    setOpen(false);
  };

  const onApplyFilter = (selectedYear: number) => {
    setOpen(!!selectedYear);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const filterData = (data) => {
    return data
  };

  return (
    <>
      <ImportCountDefault open={open} handleClose={handleClose} />

      <Stack flexDirection={'row'} alignItems={'center'} justifyContent={'space-between'}>
        <Stack flexDirection={'row'} alignItems={'center'}>
          <ArrowBack sx={{ cursor: 'pointer' }} onClick={() => navigate(routeBack)} />
          <TitlePages title={'Termômetro frequência'} showInterrogation={true}/>
        </Stack>

        <Button
          onClick={() => navigate('/configuracoes-avancadas-termometro-frequencia', { state: { id, name } })}
          variant="contained"
          sx={{ borderRadius: '24px', fontSize: '14px', height: '37px', color: '#fff', 
            background: '#15AB51',
            fontWeight: 700,
            textTransform: 'capitalize',
          }}
        >
          Configurações Avançadas
        </Button>
      </Stack>

      {isError && (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                refetch();
              }}
            >
              Atualizar
            </Button>
          }
        >
          Não foi possível carregar as informações!
        </Alert>
      )}
      <Stack flexDirection={'column'} gap={2}>
        {summary && isFetched && (
          <Stack flexDirection={'row'} gap={'8px'}>
            <KpiComponent
              title={'Quantidade de alunos no Score baixo '}
              value={summary?.students_low_amount || '-'}
              // value={"100"}
              bgColor="#FFFFFF"
            />
            <KpiComponent
              title={'Quantidade de alunos no Score Intermediário'}
              value={summary?.students_intermediary_amount || '-'}
              // value={"100"}
              bgColor="#FFFFFF"
            />
            <KpiComponent
              title={'Quantidade de alunos no Score alto'}
              value={summary?.students_high_amount || '-'}
              // value={"100"}
              bgColor="#FFFFFF"
            />

            <KpiComponent
              title={'Quantidade de alunos nas Escolinhas'} 
              value={summary?.count_students_without_boletim?.escolinhas || '-'}
              // value={"100"}
              bgColor="#FFFFFF"
            />


            <KpiComponent
              title={'Termômetro Eduzinho'}
              value={summary?.year_score || '-'}
              tooltipText={"O Eduzinho é calculado como a média dos scores frequências de cada aluno ao longo do ano atual."}
              bgColor="#FFFFFF"
            />
          </Stack>
        )}

        <Stack bgcolor={'#FFFFFF'} borderRadius={2} paddingLeft={5} paddingRight={5} paddingTop={2} paddingBottom={2}>
          <Stack>
            <Thermometer 
              score={summary?.year_score || 0}
            />
          </Stack>
        </Stack>

        <TextField
          variant="outlined"
          placeholder="Pesquisar por nome do aluno"
          inputRef={inputRef}
          sx={{
            '.MuiOutlinedInput-root': { borderRadius: '40px', padding: '0 !important', backgroundColor: '#FFF' }, // Remove padding
            '.MuiOutlinedInput-input': { padding: '10px' },
            maxWidth: '500px',
            width: '100%',
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment
                position="end"
                sx={{
                  p: 0,
                  m: 0,
                }}
              >
                <Box
                  component="button"
                  type="button"
                  sx={{
                    border: 'none',
                    outline: 'none',
                    backgroundColor: '#F0F0F0',
                    borderRadius: '50%',
                    width: '35px',
                    height: '35px',
                    display: 'flex',
                    marginRight: '10px',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'background 0.2s, box-shadow 0.2s',
                    boxShadow: '0 0 0 0 transparent',
                    '&:hover': {
                      boxShadow: '0 0 0 2px #e0e0e0',
                    },
                  }}
                  onClick={() => {
                    if (inputRef.current) {
                      inputRef.current.blur();
                    }

                    setSearchQuery(inputRef.current?.value || '');
                    refetch();
                  }}
                  tabIndex={0}
                >
                  <Search sx={{ color: '#4C4C4C' }} />
                </Box>
              </InputAdornment>
            ),
          }}
        />

        {isFetching && (
          <Grid container spacing={'20px'} justifyContent="center" alignItems="center" sx={{ minHeight: '120px', marginBottom: '20px' }}>
            <Grid item sm={4} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box><CircularProgress /></Box>
            </Grid>
            <Grid item sm={4} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box><CircularProgress /></Box>
            </Grid>
            <Grid item sm={4} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box><CircularProgress /></Box>
            </Grid>
          </Grid>
        )}
      
        { isFetched && !isFetching &&  <Grid container spacing={'20px'}>
          <Grid item sm={4}>
            <TableTermometro
              type={'high'}
              data={filterData(summary?.students_list.students_in_high ?? [])}
              route='/termometro-frequencia/aluno/detalhe'
            />
          </Grid>
          <Grid item sm={4}>
            <TableTermometro
              route='/termometro-frequencia/aluno/detalhe'
              type={'intermediary'}
              data={filterData(summary?.students_list.students_in_intermediary ?? [])}
            />
          </Grid>
          <Grid item sm={4}>
            <TableTermometro
              route='/termometro-frequencia/aluno/detalhe'
              type={'low'}
              data={filterData(summary?.students_list.students_in_low?.slice(0, 200) ?? [])} />
          </Grid>
        </Grid>}
      </Stack>
    </>
  );
};
